import { m } from 'framer-motion'
// @mui
import { alpha } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Avatar from '@mui/material/Avatar'
import Divider from '@mui/material/Divider'
import MenuItem from '@mui/material/MenuItem'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
// routes
import { paths } from 'src/routes/paths'
import { useRouter } from 'src/routes/hook'
// auth
import { useAuthContext } from 'src/auth/hooks'
// components
import Iconify from 'src/components/iconify'
import { varHover } from 'src/components/animate'
import { useSnackbar } from 'src/components/snackbar'
import CustomPopover, { usePopover } from 'src/components/custom-popover'
import { useUserStore } from 'src/store/user'

// ----------------------------------------------------------------------

const ROLE_LABEL: Record<string, string> = {
  admin: 'Administrador',
  franchisee: 'Franqueado',
  user: 'Usuário',
}

const OPTIONS = [
  {
    label: 'Início',
    icon: 'solar:home-2-bold-duotone',
    linkTo: '/dashboard',
  },
  {
    label: 'Meu perfil',
    icon: 'solar:user-id-bold-duotone',
    linkTo: paths.dashboard.settings,
  },
  {
    label: 'Segurança',
    icon: 'solar:shield-keyhole-bold-duotone',
    linkTo: `${paths.dashboard.settings}?tab=security`,
  },
]

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const router = useRouter()

  const { user } = useUserStore()

  const { logout } = useAuthContext()

  const { enqueueSnackbar } = useSnackbar()

  const popover = usePopover()

  const handleLogout = async () => {
    try {
      await logout()
      popover.onClose()
      router.replace('/')
    } catch (error) {
      console.error(error)
      enqueueSnackbar('Não foi possível sair. Tente novamente.', { variant: 'error' })
    }
  }

  const handleClickItem = (path: string) => {
    popover.onClose()
    router.push(path)
  }

  const initials = (user?.name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase())
    .join('')

  const role = (user as any)?.role as string | undefined
  const roleLabel = role ? ROLE_LABEL[role] || role : undefined

  return (
    <>
      <IconButton
        component={m.button}
        whileTap='tap'
        whileHover='hover'
        variants={varHover(1.05)}
        onClick={popover.onOpen}
        sx={{
          p: 0.5,
          borderRadius: '50%',
          background: (theme) =>
            popover.open
              ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
              : alpha('#019BD6', 0.08),
          transition: 'all 0.2s',
          '&:hover': {
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          },
        }}
      >
        <Avatar
          src={user?.photo as string | undefined}
          alt={user?.name}
          sx={{
            width: 36,
            height: 36,
            fontSize: 14,
            fontWeight: 700,
            border: (theme) => `solid 2px ${theme.palette.background.paper}`,
            bgcolor: 'primary.main',
            color: 'common.white',
          }}
        >
          {initials || user?.name?.[0]?.toUpperCase()}
        </Avatar>
      </IconButton>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        sx={{ width: 280, p: 0, borderRadius: 2, overflow: 'hidden' }}
      >
        <Box
          sx={{
            position: 'relative',
            px: 2.5,
            pt: 3,
            pb: 2,
            color: 'common.white',
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: -30,
              right: -30,
              width: 90,
              height: 90,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.12)',
            }}
          />
          <Stack direction='row' alignItems='center' spacing={1.5}>
            <Avatar
              src={user?.photo as string | undefined}
              alt={user?.name}
              sx={{
                width: 52,
                height: 52,
                fontSize: 18,
                fontWeight: 700,
                border: '2px solid rgba(255,255,255,0.7)',
                bgcolor: 'rgba(255,255,255,0.2)',
              }}
            >
              {initials || user?.name?.[0]?.toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant='subtitle1' fontWeight={700} noWrap>
                {user?.name || 'Usuário'}
              </Typography>
              <Typography variant='caption' sx={{ opacity: 0.85, display: 'block' }} noWrap>
                {user?.email}
              </Typography>
              {roleLabel && (
                <Box
                  sx={{
                    mt: 0.5,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 0.75,
                    py: 0.125,
                    borderRadius: 0.75,
                    bgcolor: 'rgba(255,255,255,0.2)',
                  }}
                >
                  <Iconify icon='solar:shield-user-bold' width={11} />
                  <Typography variant='caption' fontWeight={600} sx={{ fontSize: 10 }}>
                    {roleLabel}
                  </Typography>
                </Box>
              )}
            </Box>
          </Stack>
        </Box>

        <Stack sx={{ p: 1 }}>
          {OPTIONS.map((option) => (
            <MenuItem
              key={option.label}
              onClick={() => handleClickItem(option.linkTo)}
              sx={{
                borderRadius: 1,
                py: 1,
                gap: 1.5,
                fontSize: 14,
                fontWeight: 500,
                color: 'text.primary',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Iconify icon={option.icon} width={20} sx={{ color: 'text.secondary' }} />
              {option.label}
            </MenuItem>
          ))}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem
          onClick={handleLogout}
          sx={{
            m: 1,
            borderRadius: 1,
            py: 1,
            gap: 1.5,
            fontSize: 14,
            fontWeight: 600,
            color: 'error.main',
            '&:hover': { bgcolor: (theme) => alpha(theme.palette.error.main, 0.08) },
          }}
        >
          <Iconify icon='solar:logout-3-bold-duotone' width={20} />
          Sair
        </MenuItem>
      </CustomPopover>
    </>
  )
}
